import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { AppState } from '../../store/states/app.state';
import { UserState } from '../../store/states/user.state';
import { StoreName } from '../../store/definitions/store.constants';
import { UserType } from '../../permissions/definitions/permissions.enums';
import { StorageService } from '../../services/storage/storage.service';
import { Module } from '../../models/user/user.model';
import {
  FINANCIAL_HUB_MODULE,
  FINANCIAL_HUB_SECTIONS,
  FINANCIAL_HUB_FALLBACK_PATH,
} from '../financial-hub.constants';

/**
 * Generic hierarchical access guard for Financial Hub. Reads the target module slug from
 * `route.data.module` (the root route and each section wrapper declare their own) and grants
 * access when the user holds that slug OR any descendant of it — permissions are a tree
 * (`financial-hub` -> `financial-hub/companies` -> `.../kam-creator`), so a leaf role under a
 * section grants access to the section and to the root.
 *
 * On denial it redirects to the first section the user CAN access (see FINANCIAL_HUB_SECTIONS)
 * rather than bouncing out of the module; only a user with no Financial Hub access at all is
 * sent to `/account`.
 */
@Injectable()
export class FinancialHubAccessGuard implements CanActivate {
  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly storageService: StorageService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const targetModule: string = route.data.module || FINANCIAL_HUB_MODULE;

    return this.store.pipe(
      select(StoreName.user),
      take(1),
      map((userState: UserState) => {
        const user: UserState = Object.keys(userState).length
          ? userState
          : this.storageService.getUserAction() as unknown as UserState;

        if (user.user_type === UserType.admin) {
          return true;
        }

        const modules = user.modules || [];

        if (this.hasModuleAccess(modules, targetModule)) {
          return true;
        }

        // No access to the requested section: send the user to the first section they can access,
        // or out to /account if they have none under Financial Hub.
        const fallbackSection = FINANCIAL_HUB_SECTIONS.find((section) =>
          this.hasModuleAccess(modules, section.module),
        );
        this.router.navigate([fallbackSection ? fallbackSection.path : FINANCIAL_HUB_FALLBACK_PATH]);
        return false;
      }),
    );
  }

  // Prefix match: the user holds `modulePrefix` itself or any descendant slug under it.
  private hasModuleAccess(modules: Module[], modulePrefix: string): boolean {
    return modules.some(
      (module: Module) => module.slug === modulePrefix || module.slug.startsWith(`${modulePrefix}/`),
    );
  }
}
